import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { PushToggle } from "@/components/PushPermissionBanner";

const POLL_MS = 30000;

export default function NotificationsBell() {
  const { lang, theme, dir } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );
  const ref = useRef(null);

  // Track viewport changes to switch between desktop dropdown and mobile bottom sheet.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const T = lang === "ar"
    ? {
        title: "الإشعارات", empty: "لا توجد إشعارات بعد",
        mark_all: "تعليم الكل كمقروء", view_all: "كل الإشعارات",
        a_min_ago: (n) => `قبل ${n} دقيقة`,
        hours_ago: (n) => `قبل ${n} ساعة`,
        days_ago: (n) => `قبل ${n} يوم`,
        now: "الآن",
      }
    : {
        title: "Notifications", empty: "No notifications yet",
        mark_all: "Mark all read", view_all: "View all",
        a_min_ago: (n) => `${n}m ago`,
        hours_ago: (n) => `${n}h ago`,
        days_ago: (n) => `${n}d ago`,
        now: "Now",
      };

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* ignore */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=15");
      setItems(data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchCount]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  const togglePanel = async () => {
    const willOpen = !open;
    if (willOpen) {
      await fetchList();
      // WhatsApp-style behaviour: opening the bell clears the unread badge.
      // We mark all as read immediately on the backend, but keep the local
      // `unread` flag for ~1.2s so the user can still see which items are new.
      try {
        const { data: cnt } = await api.get("/notifications/unread-count");
        if ((cnt?.count || 0) > 0) {
          await api.post("/notifications/read-all");
          setCount(0);
          // After the user has had a moment to glance at the new items,
          // visually fade them into the "read" state.
          setTimeout(() => {
            setItems((arr) => arr.map((n) => ({ ...n, is_read: true })));
          }, 1200);
        }
      } catch { /* ignore */ }
    }
    setOpen(willOpen);
  };

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((arr) => arr.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setCount((c) => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  const openItem = async (n) => {
    // Mark as read, navigate to the linked entity, close the panel
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((arr) => arr.map((n) => ({ ...n, is_read: true })));
      setCount(0);
    } catch { /* ignore */ }
  };

  const css = {
    wrap: { position: "relative" },
    btn: {
      position: "relative",
      width: "38px",
      height: "38px",
      borderRadius: "50%",
      background: "transparent",
      border: `1px solid ${theme.border}`,
      color: theme.text,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "16px",
      transition: "all 150ms ease",
    },
    badge: {
      position: "absolute",
      top: "-4px",
      [dir === "rtl" ? "left" : "right"]: "-4px",
      minWidth: "18px",
      height: "18px",
      borderRadius: "9px",
      background: "#ef4444",
      color: "#fff",
      fontSize: "10px",
      fontWeight: 800,
      padding: "0 5px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 0 0 2px " + theme.headerBg,
    },
    panel: isMobile ? {
      // Mobile — full-width bottom sheet. Use an explicit height (85vh) so
      // we don't depend on `bottom` resolving correctly inside any sticky
      // ancestors (which can clip fixed children in some browsers).
      position: "fixed",
      top: "15vh",
      left: 0,
      right: 0,
      width: "100vw",
      maxWidth: "100vw",
      height: "85vh",
      overflowY: "auto",
      background: theme.surface,
      borderTop: `1px solid ${theme.borderAccent}`,
      borderRadius: "18px 18px 0 0",
      boxShadow: "0 -8px 32px rgba(0,0,0,0.45)",
      zIndex: 9000,
      animation: "vtaNotifSheet 280ms cubic-bezier(.2,.8,.2,1)",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
    } : {
      // Desktop — anchored next to the bell
      position: "absolute",
      top: "44px",
      [dir === "rtl" ? "left" : "right"]: 0,
      width: "360px",
      maxWidth: "92vw",
      maxHeight: "70vh",
      overflowY: "auto",
      background: theme.surface,
      border: `1px solid ${theme.borderAccent}`,
      borderRadius: "12px",
      boxShadow: theme.cardShadow,
      zIndex: 9000,
    },
    backdrop: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      zIndex: 8999,
      animation: "vtaNotifFade 200ms ease",
    },
    handle: {
      width: "42px", height: "4px",
      background: "rgba(255,255,255,0.28)",
      borderRadius: "2px",
      margin: "8px auto 0",
    },
    head: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 14px",
      borderBottom: `1px solid ${theme.borderSoft}`,
      background: theme.headerBg,
      position: "sticky",
      top: 0,
    },
    title: { fontWeight: 800, color: theme.gold, fontSize: "13px" },
    link: { background: "transparent", border: "none", color: theme.text, fontSize: "11px", fontWeight: 700, cursor: "pointer" },
    item: (unread) => ({
      padding: "12px 14px",
      borderBottom: `1px solid ${theme.borderSoft}`,
      cursor: "pointer",
      background: unread ? (theme.name === "dark" ? "rgba(212,175,55,0.06)" : "rgba(184,144,42,0.08)") : "transparent",
      display: "flex",
      gap: "10px",
    }),
    dot: (unread) => ({
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      marginTop: "6px",
      background: unread ? theme.gold : "transparent",
      flexShrink: 0,
    }),
    body: { flex: 1, minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" },
    itemTitle: { fontWeight: 800, fontSize: "13px", color: theme.text, marginBottom: "4px", lineHeight: 1.5 },
    itemMsg: { fontSize: "12px", color: theme.textSoft, lineHeight: 1.6 },
    itemTime: { fontSize: "10px", color: theme.textMuted, marginTop: "6px" },
    empty: { padding: "32px 16px", textAlign: "center", color: theme.textMuted, fontSize: "13px" },
    loading: { padding: "16px", textAlign: "center", color: theme.textMuted, fontSize: "12px" },
  };

  if (!user) return null;

  return (
    <div style={css.wrap} ref={ref}>
      <button
        type="button"
        onClick={togglePanel}
        style={css.btn}
        title={T.title}
        data-testid="notif-bell"
        aria-label={T.title}
      >
        <span aria-hidden style={{ color: theme.gold, display: "inline-flex" }}>
          <Bell size={18} strokeWidth={2.4} />
        </span>
        {count > 0 && (
          <span style={css.badge} data-testid="notif-badge">{count > 99 ? "99+" : count}</span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile-only backdrop dimmer */}
          {isMobile && (
            <div
              onClick={() => setOpen(false)}
              data-testid="notif-backdrop"
              style={css.backdrop}
            />
          )}
          <div
            style={css.panel}
            data-testid="notif-panel"
          >
          <style>{`
            @keyframes vtaNotifFade { from { opacity: 0 } to { opacity: 1 } }
            @keyframes vtaNotifSheet { from { transform: translateY(100%); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
          `}</style>
          {isMobile && <div style={css.handle} />}
          <div style={css.head}>
            <div style={css.title}>{T.title}</div>
            {items.some((n) => !n.is_read) && (
              <button onClick={markAll} style={css.link} data-testid="notif-mark-all">
                {T.mark_all}
              </button>
            )}
          </div>
          {/* Push enable toggle — visible inside the bell dropdown so every
              user (any device, iOS PWA included) can switch it on at any time. */}
          <div style={{
            padding: "10px 14px",
            borderBottom: `1px solid ${theme.borderSoft}`,
            background: theme.surface,
            display: "flex", justifyContent: "space-between",
            alignItems: "center", gap: 8, flexWrap: "wrap",
          }}>
            <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.6, flex: 1, minWidth: 140 }}>
              {lang === "ar"
                ? "تنبيهات الجوال والكمبيوتر — حتى لو التطبيق مغلق"
                : "Phone & desktop alerts — even when the app is closed"}
            </div>
            <PushToggle />
          </div>
          {loading ? (
            <div style={css.loading}>…</div>
          ) : items.length === 0 ? (
            <div style={css.empty} data-testid="notif-empty">{T.empty}</div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                style={css.item(!n.is_read)}
                onClick={() => openItem(n)}
                data-testid={`notif-item-${n.id}`}
              >
                <span style={css.dot(!n.is_read)} />
                <div style={css.body}>
                  <div style={css.itemTitle}>{n.title}</div>
                  <div style={css.itemMsg}>{n.message}</div>
                  <div style={css.itemTime}>{formatTime(n.created_at, T)}</div>
                </div>
              </div>
            ))
          )}
        </div>
        </>
      )}
    </div>
  );
}

function formatTime(iso, T) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return T.now;
  if (m < 60) return T.a_min_ago(m);
  const h = Math.floor(m / 60);
  if (h < 24) return T.hours_ago(h);
  const days = Math.floor(h / 24);
  return T.days_ago(days);
}
